import { useEffect, useRef } from 'react';
import type { Card, CardField, FieldModes, Language } from '../../../shared/types';
import type { Speech } from '../hooks/useSpeech';

interface FlashcardProps {
  card: Card;
  fields: FieldModes;
  language: Language;
  speech: Speech;
  speechRate: number;
  autoPlayAudio: boolean;
  flipped: boolean;
}

function SpeakerIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M11 5 6 9H3v6h3l5 4V5Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M15.5 8.5a4.5 4.5 0 0 1 0 7M18.5 5.5a8.5 8.5 0 0 1 0 13"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * One flashcard, rendered from the learner's field configuration.
 *
 * There is no fixed front and back. Each of the four fields is independently
 * `prompt`, `answer` or `hidden`, so the same card is a reading drill, a
 * listening drill or a production drill depending only on this configuration.
 * The component's whole job is to put each field on the side it was assigned
 * and to leave out the ones set to `hidden`.
 */
export default function Flashcard({
  card,
  fields,
  language,
  speech,
  speechRate,
  autoPlayAudio,
  flipped,
}: FlashcardProps) {
  const audioIsPrompt = fields.audio === 'prompt';
  const spokenFor = useRef<string | null>(null);

  // Auto-play once per card, not once per render — and never on the flip, which
  // would talk over someone reading the answer.
  useEffect(() => {
    if (!autoPlayAudio || fields.audio === 'hidden') return;
    if (!speech.voiceAvailable) return;
    if (spokenFor.current === card.id) return;
    spokenFor.current = card.id;
    speech.speak(card.target, speechRate);
  }, [card.id, card.target, autoPlayAudio, fields.audio, speech, speechRate]);

  const play = () => speech.speak(card.target, speechRate);

  const show = (field: CardField, side: 'prompt' | 'answer') => fields[field] === side;

  const hasTranslit = Boolean(language.transliteration && card.translit);

  const renderAudio = (side: 'prompt' | 'answer') => {
    if (!show('audio', side)) return null;
    if (!speech.voiceAvailable) {
      // Only reachable when audio is the prompt — otherwise the field is simply
      // dropped. Saying so is better than a dead button.
      return side === 'prompt' ? (
        <p className="card-audio-missing">
          No {language.name} voice is installed on this device, so this card cannot be played.
          Change the card setup to show the written form instead.
        </p>
      ) : null;
    }
    return (
      <button
        type="button"
        onClick={play}
        className={side === 'prompt' ? 'audio-button audio-button--large' : 'audio-button'}
        aria-label={`Play the ${language.name} audio`}
      >
        <SpeakerIcon size={side === 'prompt' ? 34 : 18} />
        {side === 'prompt' && <span className="audio-button-hint">Play</span>}
      </button>
    );
  };

  const renderField = (field: CardField, side: 'prompt' | 'answer') => {
    if (field === 'audio') return renderAudio(side);
    if (!show(field, side)) return null;

    switch (field) {
      case 'target':
        return (
          <p key="target" className={`card-target card-target--${language.script}`} lang={language.code}>
            {card.target}
          </p>
        );
      case 'translit':
        return hasTranslit ? (
          <p key="translit" className="card-translit">
            {card.translit}
          </p>
        ) : null;
      case 'translation':
        return (
          <p key="translation" className="card-translation">
            {card.translation}
          </p>
        );
      default:
        return null;
    }
  };

  const promptSide = (['audio', 'target', 'translit', 'translation'] as CardField[])
    .map((field) => renderField(field, 'prompt'))
    .filter(Boolean);

  const answerSide = (['target', 'translit', 'translation', 'audio'] as CardField[])
    .map((field) => renderField(field, 'answer'))
    .filter(Boolean);

  return (
    <div className={`flashcard${flipped ? ' flashcard--flipped' : ''}`}>
      <div className="flashcard-prompt">{promptSide}</div>

      {flipped && (
        <>
          <hr className="flashcard-divider" />
          <div className="flashcard-answer">
            {answerSide}

            {card.note && <p className="card-note">{card.note}</p>}

            {card.example && (
              <div className="card-example">
                <span className="card-example-target" lang={language.code}>
                  {card.example.target}
                </span>
                {card.example.translit && (
                  <span className="card-example-translit">{card.example.translit}</span>
                )}
                <span className="card-example-translation">{card.example.translation}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* A listening card with nothing flipped yet is otherwise a blank box. */}
      {!flipped && audioIsPrompt && promptSide.length === 1 && (
        <p className="flashcard-listen-hint">Listen, then reveal the answer.</p>
      )}
    </div>
  );
}
